// Filters: Binnen/Buiten · Veel/Weinig energie · 1/2/3+ spelers · Favorieten, en daaronder
// "Hoeveel tijd hebben we?". Mobiel horizontaal scrollbaar met snap en een zachte rand-fade
// waar nog chips verborgen zijn.
import { Clock, Heart, House, Sofa, Trees, Zap } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ToggleChip } from '../../components/ui/ToggleChip';
import { TIME_BUDGET_OPTIONS, type TimeBudget } from '../../domain/suggest';
import type { ActivityFilters } from '../../domain/types';

interface FilterChipsProps {
  filters: ActivityFilters;
  onChange: (next: ActivityFilters) => void;
  timeBudget: TimeBudget;
  onTimeBudgetChange: (next: TimeBudget) => void;
  onlyFavorites: boolean;
  onOnlyFavoritesChange: (next: boolean) => void;
}

function toggle<K extends keyof ActivityFilters>(filters: ActivityFilters, key: K, value: NonNullable<ActivityFilters[K]>): ActivityFilters {
  return { ...filters, [key]: filters[key] === value ? null : value };
}

function Divider() {
  return <span aria-hidden="true" className="my-2.5 w-px shrink-0 self-stretch bg-slate-900/10 dark:bg-white/10" />;
}

/** Horizontaal scrollbare rij met edge-fade (mobiel); wrapt vanaf sm. */
function ScrollRow({ label, children }: { label: string; children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const start = el.scrollLeft > 4;
    const end = max - el.scrollLeft > 4;
    setEdges((cur) => (cur.start === start && cur.end === end ? cur : { start, end }));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener('scroll', updateEdges, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateEdges) : null;
    ro?.observe(el);
    window.addEventListener('resize', updateEdges);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      ro?.disconnect();
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges]);

  const fade = 28;
  const mask = `linear-gradient(to right, ${edges.start ? 'transparent' : '#000'} 0, #000 ${edges.start ? fade : 0}px, #000 calc(100% - ${
    edges.end ? fade : 0
  }px), ${edges.end ? 'transparent' : '#000'} 100%)`;

  return (
    <div
      ref={scrollerRef}
      role="group"
      aria-label={label}
      className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-2 overflow-x-auto px-4 py-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
      style={{ maskImage: mask, WebkitMaskImage: mask }}
    >
      {children}
    </div>
  );
}

export function FilterChips({ filters, onChange, timeBudget, onTimeBudgetChange, onlyFavorites, onOnlyFavoritesChange }: FilterChipsProps) {
  const set = (next: ActivityFilters) => onChange(next);

  return (
    <div className="space-y-3">
      <ScrollRow label="Filters">
        <div role="group" aria-label="Binnen of buiten" className="flex shrink-0 gap-2">
          <ToggleChip icon={House} pressed={filters.type === 'Binnen'} onToggle={() => set(toggle(filters, 'type', 'Binnen'))} title="Binnenactiviteiten">
            Binnen
          </ToggleChip>
          <ToggleChip icon={Trees} pressed={filters.type === 'Buiten'} onToggle={() => set(toggle(filters, 'type', 'Buiten'))} title="Buitenactiviteiten">
            Buiten
          </ToggleChip>
        </div>
        <Divider />
        <div role="group" aria-label="Energie" className="flex shrink-0 gap-2">
          <ToggleChip
            icon={Zap}
            pressed={filters.energie === 'Veel energie'}
            onToggle={() => set(toggle(filters, 'energie', 'Veel energie'))}
            ariaLabel="Veel energie"
          >
            Veel energie
          </ToggleChip>
          <ToggleChip
            icon={Sofa}
            pressed={filters.energie === 'Rustig'}
            onToggle={() => set(toggle(filters, 'energie', 'Rustig'))}
            ariaLabel="Weinig energie"
            title="Weinig energie / rustig"
          >
            Weinig energie
          </ToggleChip>
        </div>
        <Divider />
        <div role="group" aria-label="Aantal spelers" className="flex shrink-0 gap-2">
          <ToggleChip pressed={filters.personen === '1'} onToggle={() => set(toggle(filters, 'personen', '1'))}>
            1 speler
          </ToggleChip>
          <ToggleChip pressed={filters.personen === '2'} onToggle={() => set(toggle(filters, 'personen', '2'))}>
            2 spelers
          </ToggleChip>
          <ToggleChip
            pressed={filters.personen === '3+'}
            onToggle={() => set(toggle(filters, 'personen', '3+'))}
            ariaLabel="3 of meer spelers"
          >
            3+ spelers
          </ToggleChip>
        </div>
        <Divider />
        <div className="flex shrink-0 gap-2">
          <ToggleChip icon={Heart} pressed={onlyFavorites} onToggle={() => onOnlyFavoritesChange(!onlyFavorites)} title="Alleen favorieten">
            Favorieten
          </ToggleChip>
        </div>
      </ScrollRow>

      <div className="space-y-1">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <Clock size={16} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
          <span>Hoeveel tijd hebben we?</span>
        </p>
        <ScrollRow label="Hoeveel tijd hebben we?">
          <ToggleChip pressed={timeBudget === null} onToggle={() => onTimeBudgetChange(null)}>
            Maakt niet uit
          </ToggleChip>
          {TIME_BUDGET_OPTIONS.map((o) => (
            <ToggleChip
              key={o.value}
              pressed={timeBudget === o.value}
              onToggle={() => onTimeBudgetChange(timeBudget === o.value ? null : o.value)}
              ariaLabel={`Maximaal ${o.label}`}
            >
              {o.label}
            </ToggleChip>
          ))}
        </ScrollRow>
      </div>
    </div>
  );
}
